import { IMAGE_EXTENSIONS } from '@/shared/config/file-restrictions'

export const ossThumbnailTiers = [
  { id: 'T0', width: 160, height: 90 },
  { id: 'T1', width: 320, height: 180 },
  { id: 'T2', width: 640, height: 360 },
  { id: 'T3', width: 960, height: 540 },
  { id: 'T4', width: 1280, height: 720 },
  { id: 'T5', width: 200, height: 200 },
] as const

export type OssThumbnailTier = (typeof ossThumbnailTiers)[number]

export const ossPortraitThumbnailTiers = [
  { id: 'P0', width: 90, height: 160 },
  { id: 'P1', width: 180, height: 320 },
  { id: 'P2', width: 360, height: 640 },
  { id: 'P3', width: 720, height: 1280 },
  { id: 'P4', width: 1024, height: 2048 },
] as const

const ossLandscapeDisplayThumbnailTiers = [
  ossThumbnailTiers[0],
  ossThumbnailTiers[1],
  ossThumbnailTiers[2],
  ossThumbnailTiers[3],
  ossThumbnailTiers[4],
] as const

type OssThumbnailSize = {
  width: number
  height: number
}

interface OssImageDisplayThumbnailOptions {
  devicePixelRatio?: number
  displayHeight: number
  displayWidth: number
  zoom?: number
}

const MAX_EFFECTIVE_DEVICE_PIXEL_RATIO = 2
const DUH_OSS_HOSTS = new Set(['img.duh.com.cn', 'hwimg.duh.com.cn'])
const QIANLU_OSS_HOSTS = new Set(['canvas.qianlu.cc', 'res.qianlu.net'])
const AI_GENERATED_URL_MARKER = 'res/userdata/ud/'
// let aiGeneratedImageWatermarkEnabled = true

function isFinitePositiveNumber(value?: number) {
  return Number.isFinite(value) && (value ?? 0) > 0
}

const IMAGE_EXT_REGEX = new RegExp(
  `\\.(${IMAGE_EXTENSIONS.map(e => e.replace('.', '')).join('|')}|gif)(\\?|#|$)`,
  'i'
)

function isLikelyImageUrl(url: string) {
  return IMAGE_EXT_REGEX.test(url) || url.includes('x-oss-process=image/')
}

function normalizeOssHost(hostname: string) {
  if (hostname === 'hwimg.duh.com.cn') {
    return 'img.duh.com.cn'
  }

  return hostname
}

export function isOssProcessableImageUrl(url: string | null | undefined) {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) {
    return false
  }

  try {
    const parsed = new URL(url)
    const hostname = normalizeOssHost(parsed.hostname)
    if (DUH_OSS_HOSTS.has(hostname) || QIANLU_OSS_HOSTS.has(hostname)) {
      return isLikelyImageUrl(url)
    }
  } catch {
    return false
  }

  return false
}

export function isAiGeneratedImageUrl(url: string | null | undefined) {
  return typeof url === 'string' && url.includes(AI_GENERATED_URL_MARKER)
}

function resolveOssThumbnailTierForDisplay({
  devicePixelRatio,
  displayHeight,
  displayWidth,
  zoom,
}: OssImageDisplayThumbnailOptions) {
  const effectiveZoom = isFinitePositiveNumber(zoom) ? zoom! : 1
  const effectiveDevicePixelRatio = Math.min(
    MAX_EFFECTIVE_DEVICE_PIXEL_RATIO,
    isFinitePositiveNumber(devicePixelRatio) ? devicePixelRatio! : 1,
  )
  const targetWidth = displayWidth * effectiveZoom * effectiveDevicePixelRatio
  const targetHeight = displayHeight * effectiveZoom * effectiveDevicePixelRatio
  const thumbnailTiers = targetHeight > targetWidth
    ? ossPortraitThumbnailTiers
    : ossLandscapeDisplayThumbnailTiers

  return (
    thumbnailTiers.find((tier) => tier.width >= targetWidth && tier.height >= targetHeight)
    ?? thumbnailTiers[thumbnailTiers.length - 1]
  )
}

function removeExistingOssProcess(search: string) {
  return search
    .replace(/^\?/, '')
    .split('&')
    .filter(Boolean)
    .filter((part) => !part.startsWith('x-oss-process='))
}

function appendOssProcess(url: string, process: string) {
  try {
    const parsed = new URL(url)
    const nextQueryParts = [...removeExistingOssProcess(parsed.search), `x-oss-process=${process}`]
    const nextQuery = nextQueryParts.join('&')
    const baseUrl = `${parsed.protocol}//${parsed.host}${parsed.pathname}`

    return `${baseUrl}${nextQuery ? `?${nextQuery}` : ''}${parsed.hash}`
  } catch (err) {
    // 处理相对路径或无法通过 URL 解析的字符串
    const [pathAndSearch, hash] = url.split('#')
    const [path, search] = pathAndSearch.split('?')
    const nextQueryParts = [...removeExistingOssProcess(`?${search || ''}`), `x-oss-process=${process}`]
    const nextQuery = nextQueryParts.join('&')
    
    return `${path}${nextQuery ? `?${nextQuery}` : ''}${hash ? `#${hash}` : ''}`
  }
}

export function setAiGeneratedImageWatermarkEnabled(_enabled: boolean) {
  // aiGeneratedImageWatermarkEnabled = _enabled
}

export function buildOssWatermarkUrl(url: string) {
  return url
}

export function buildOssThumbnailUrl(url: string, tier: OssThumbnailSize) {
  const process = [`image/resize,m_lfit,w_${tier.width},h_${tier.height}`]
  process.push('format,webp')

  return appendOssProcess(
    url,
    process.join('/'),
  )
}

export function resolveOssThumbnailUrl(url: string | null | undefined, tier: OssThumbnailSize) {
  if (!url) return undefined
  return buildOssThumbnailUrl(url, tier)
}

export function resolveOssDisplayThumbnailUrl(
  url: string | null | undefined,
  options: OssImageDisplayThumbnailOptions,
) {
  if (!url) {
    return undefined
  }

  if (!isFinitePositiveNumber(options.displayWidth) || !isFinitePositiveNumber(options.displayHeight)) {
    return isOssProcessableImageUrl(url) ? buildOssWatermarkUrl(url) : url
  }

  if (!isOssProcessableImageUrl(url)) {
    return url
  }

  const tier = resolveOssThumbnailTierForDisplay(options)
  return buildOssThumbnailUrl(url, tier)
}

const EDITOR_PREVIEW_MAX_SIZE = 4096

export function buildOssEditorPreviewUrl(
  url: string | null | undefined,
  sourceWidth?: number,
  sourceHeight?: number,
): string | undefined {
  if (!url) return undefined
  if (!isOssProcessableImageUrl(url)) return url

  const maxDimension = Math.max(sourceWidth ?? 0, sourceHeight ?? 0)
  if (maxDimension <= 0 || maxDimension <= EDITOR_PREVIEW_MAX_SIZE) {
    return url
  }

  return appendOssProcess(
    url,
    `image/resize,m_lfit,w_${EDITOR_PREVIEW_MAX_SIZE},h_${EDITOR_PREVIEW_MAX_SIZE}`,
  )
}

export function buildVideoFirstFrameUrl(url: string) {
  if (!url || url.startsWith('blob:') || url.startsWith('data:') || url.includes('x-oss-process=video/snapshot')) {
    return url
  }

  return appendOssProcess(url, 'video/snapshot,t_0,f_jpg')
}

export function buildVideoPosterPreviewUrl(url: string, tier: OssThumbnailSize, options?: { fitBy?: 'width' | 'height' }) {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) {
    return url
  }

  const width = options?.fitBy === 'height' ? 0 : tier.width
  const height = options?.fitBy === 'height' ? tier.height : 0
  return appendOssProcess(url, `video/snapshot,t_0,f_jpg,w_${width},h_${height},m_fast`)
}

export function resolveOssVideoPosterThumbnailUrl(
  url: string | null | undefined,
  options: OssImageDisplayThumbnailOptions,
  fitOptions?: { fitBy?: 'width' | 'height' }
) {
  if (!url) return undefined
  if (!isFinitePositiveNumber(options.displayWidth) || !isFinitePositiveNumber(options.displayHeight)) return url
  
  const tier = resolveOssThumbnailTierForDisplay(options)
  return buildVideoPosterPreviewUrl(url, tier, fitOptions)
}
